<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <h2 class="text-3xl font-display font-bold text-white">{{ $t('auth.reset_password') }}</h2>
      <p class="mt-2 text-sm text-gray-400">
        {{ $t('auth.reset_password_enter') || 'Enter your new password below' }}
      </p>
    </div>

    <!-- Form -->
    <form @submit.prevent="handleResetPassword" class="space-y-5">
      <!-- Email (readonly) -->
      <div>
        <label for="email" class="form-label">{{ $t('auth.email') }}</label>
        <input
          id="email"
          v-model="form.email"
          type="email"
          readonly
          class="form-input bg-white/5 opacity-50 cursor-not-allowed"
        />
      </div>

      <!-- Password -->
      <div>
        <label for="password" class="form-label">{{ $t('auth.password') }}</label>
        <input
          id="password"
          v-model="form.password"
          type="password"
          required
          class="form-input"
          :class="{ 'border-red-500/50': errors.password }"
          placeholder="••••••••"
        />
        <p v-if="errors.password" class="mt-1 text-sm text-red-400">{{ errors.password[0] }}</p>
      </div>

      <!-- Confirm Password -->
      <div>
        <label for="password_confirmation" class="form-label">{{ $t('auth.confirm_password') }}</label>
        <input
          id="password_confirmation"
          v-model="form.password_confirmation"
          type="password"
          required
          class="form-input"
          placeholder="••••••••"
        />
      </div>

      <!-- Submit button -->
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full btn btn-lg btn-primary bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 border-none"
        :class="{ 'opacity-50 cursor-not-allowed': isLoading }"
      >
        <div v-if="isLoading" class="flex items-center">
          <div class="spinner w-5 h-5 mr-2 border-white"></div>
          {{ $t('common.messages.loading') }}
        </div>
        <span v-else>{{ $t('auth.reset_password') }}</span>
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const form = reactive({
  token: route.query.token || '',
  email: route.query.email || '',
  password: '',
  password_confirmation: ''
})

const errors = ref({})
const isLoading = ref(false)

const handleResetPassword = async () => {
  isLoading.value = true
  errors.value = {}

  try {
    await authStore.resetPassword(form)
    toast.success(t('auth.password_reset_success'))
    router.push({ name: 'login' })
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors || {}
    } else {
      toast.error(t('common.messages.error'))
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  if (!form.token || !form.email) {
    toast.error(t('auth.invalid_reset_link'))
    router.push({ name: 'login' })
  }
})
</script>